// THIS FILE IS AN EXACT COPY OF QC/CONSTANTS in func-context, except for the QC_DB_FIELDS array

// This is for simplicity. Whatever changes you make in one, please make in the other.
// This is because we need some of these types defined for both front/back end
// of QC work. However it is a pain to import one of the backend packages into
// our front end code. thereore ui-lib gets its own version.

import map from 'lodash/map'
import union from 'lodash/union'

// used to differentiate between valid "boundary types" of inputs
export const CONTINUOUS = 'continuous'
export const DISCRETE = 'discrete'

// Informational text to describe sections in output file
export const missingnessInfoText =
  'Percentage of values missing in select fields of your input file by applicant cohort:'
export const outOfBoundsInfoText =
  'Percentage of values out of bounds in select fields of your input file by applicant cohort:'
export const summaryStatisticsInfoText = 'Summary Statistics:'

export const groupByColumnInfo = { field: 'entry_term', label: 'Entry_Term' }
export const diCodeInfo = { field: 'di_code', label: 'DI Code' }
export const entryTermInfo = { field: 'entry_term', label: 'Entry_Term' }
export const institutionNameInfo = { field: 'institutionName', label: 'Institution' }
export const jobIdInfo = { field: 'jobId', label: 'Job ID' }
export const numRecordsColumnInfo = { field: 'records', label: '#_Records' }
export const groupByColumnField = entryTermInfo.field
export const groupByColumnLabel = entryTermInfo.label

// map of valid input values per DB column

export const validValues = {
  gender: {
    type: DISCRETE,
    values: [1, 2, 3],
  },
  race: {
    type: DISCRETE,
    values: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  },
  display_score: {
    type: CONTINUOUS,
    min: 400,
    max: 1600,
    decimals: 0,
  },
  admitted: {
    type: DISCRETE,
    values: [0, 1],
  },
  enrolled: {
    type: DISCRETE,
    values: [0, 1],
  },
  first_year_gpa: {
    type: CONTINUOUS,
    min: 0.0,
    max: 4.0,
    decimals: 2,
  },
  sophomore_retention: {
    type: DISCRETE,
    values: [0, 1],
  },
}

export const COHORT_STAGES = {
  APPLICATION: '1 application',
  DECISION: '2 decision',
  ENROLLMENT: '3 enrollment',
  FYGPA: '4 fygpa',
  RETENTION: '5 retention',
}

export const histReqFields = ['admitted', 'race']
export const histEitherFields = ['hs_ceeb_cd', 'geo']
export const yoyReqFields = [...histReqFields, 'gender', 'display_score']
export const yoyEitherFields = ['geo', 'state']

export const isNotEmptyFields = ['display_score', 'gender', 'geo', 'hs_ceeb_cd', 'state']

// to avoid typos
const GEO = 'geo'
export const GEO_2010 = 'geo_2010'
export const GEO_2020 = 'geo_2020'
const HS_CEEB_CD = 'hs_ceeb_cd'
const GENDER = 'gender'
const RACE = 'race'
const DISPLAY_SCORE = 'display_score'
const ADMITTED = 'admitted'
const ENROLLED = 'enrolled'
const FIRST_YEAR_GPA = 'first_year_gpa'
const SOPHOMORE_RETENTION = 'sophomore_retention'
const STATE = 'state'
export const HIST_USABLE = 'hist_usable'
export const YOY_USABLE = 'yoy_usable'
const STAGE = 'stage'
export const REVIEWED = 'reviewed'
export const BEST = 'best'

// In order to keep things tidy in Dynamo, we are going to prefix
// each key with which group they belong to.
export const missingnessPrefix = 'm_'
export const outOfBoundsPrefix = 'oob_'
export const summaryStatsPrefix = 'ss_'
export const reviewStatusPrefix = 'rs_'
// type QC_Prefix = typeof missingnessPrefix | typeof outOfBoundsPrefix | typeof summaryStatsPrefix

// Helper function to take one of our "info" object (outOfBoundsInfo, missingnessInfo, etc)
// and create a new array consisting of the "col" column taken from each item.
// exampple: getNames(missingnessInfo, 'field') // = ['m_gender', 'm_race', 'm_display_score'...]
const getNames = (arrayOfFields, col: string): string[] => {
  return map(arrayOfFields, col)
}

// this is nice to have bc even though our "info" objects (missingness, oob, ss)
// are different, there is still a specific set of columsn we actually need from our
// mysql student table.
export const QC_DB_FIELDS = [
  GEO, // func-context/constants.ts needs to use either GEO_2010 or GEO_2020
  HS_CEEB_CD,
  GENDER,
  RACE,
  DISPLAY_SCORE,
  ADMITTED,
  ENROLLED,
  FIRST_YEAR_GPA,
  SOPHOMORE_RETENTION,
]

// admin qc also needs state from student table
export const ALL_QC_DB_FIELDS = [...QC_DB_FIELDS, STATE]

// MISSINGNESS FIELDS
export const missingnessInfo = [
  { uniqueField: `${missingnessPrefix}${GEO}`, field: GEO, label: 'Geo' },
  { uniqueField: `${missingnessPrefix}${HS_CEEB_CD}`, field: HS_CEEB_CD, label: 'HS_CEEB_code' },
  { uniqueField: `${missingnessPrefix}${GENDER}`, field: GENDER, label: 'Gender' },
  { uniqueField: `${missingnessPrefix}${RACE}`, field: RACE, label: 'Race' },
  { uniqueField: `${missingnessPrefix}${DISPLAY_SCORE}`, field: DISPLAY_SCORE, label: 'Display Score' },
  { uniqueField: `${missingnessPrefix}${ADMITTED}`, field: ADMITTED, label: 'Admitted' },
  { uniqueField: `${missingnessPrefix}${ENROLLED}`, field: ENROLLED, label: 'Enrolled' },
  { uniqueField: `${missingnessPrefix}${FIRST_YEAR_GPA}`, field: FIRST_YEAR_GPA, label: 'First_Year_GPA' },
  {
    uniqueField: `${missingnessPrefix}${SOPHOMORE_RETENTION}`,
    field: SOPHOMORE_RETENTION,
    label: 'Sophomore_Retention',
  },
]

// see comment above "getNames" function for expl. on what this returns.
export const missingnessFields = [...getNames(missingnessInfo, 'field')]
export const missingnessLabels = [...getNames(missingnessInfo, 'label')]

// 'state' used for admin when calculating hist and yoy usable
export const missingnessInfoAdmin = [...missingnessInfo, { field: `state`, label: 'State' }]
export const missingnessFieldsAdmin = [...getNames(missingnessInfoAdmin, 'field')]
// export const missingnessLabelsAdmin = [...getNames(missingnessInfoAdmin, 'label')]

// OUT OF BOUNDS FIELDS
export const outOfBoundsInfo = [
  { uniqueField: `${outOfBoundsPrefix}${GENDER}`, field: GENDER, label: 'Gender' },
  { uniqueField: `${outOfBoundsPrefix}${RACE}`, field: RACE, label: 'Race' },
  { uniqueField: `${outOfBoundsPrefix}${DISPLAY_SCORE}`, field: DISPLAY_SCORE, label: 'Display Score' },
  { uniqueField: `${outOfBoundsPrefix}${ADMITTED}`, field: ADMITTED, label: 'Admitted' },
  { uniqueField: `${outOfBoundsPrefix}${ENROLLED}`, field: ENROLLED, label: 'Enrolled' },
  { uniqueField: `${outOfBoundsPrefix}${FIRST_YEAR_GPA}`, field: FIRST_YEAR_GPA, label: 'First_Year_GPA' },
  {
    uniqueField: `${outOfBoundsPrefix}${SOPHOMORE_RETENTION}`,
    field: SOPHOMORE_RETENTION,
    label: 'Sophomore_Retention',
  },
]

// see comment above "getNames" function for expl. on what this returns.
export const outOfBoundsFields = [...getNames(outOfBoundsInfo, 'field')]
// export const outOfBoundsLabels = [...getNames(outOfBoundsInfo, 'label')]

// SUMMARY STATISTICS FIELDS
// summary stats is a little more involved, as we are interested in the frequency
// of individual values.
export const summaryStatisticsInfo = [
  { uniqueField: `${summaryStatsPrefix}${GENDER}_1`, field: GENDER, label: 'Gender 1 (Female)', value: 1 },
  { uniqueField: `${summaryStatsPrefix}${GENDER}_2`, field: GENDER, label: 'Gender 2 (Male)', value: 2 },
  { uniqueField: `${summaryStatsPrefix}${GENDER}_3`, field: GENDER, label: 'Gender 3 (Another)', value: 3 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_1`, field: RACE, label: 'Race 1 (White)', value: 1 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_2`, field: RACE, label: 'Race 2 (Black/AA)', value: 2 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_3`, field: RACE, label: 'Race 3 (Hisp./Lat.)', value: 3 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_4`, field: RACE, label: 'Race 4 (Asian)', value: 4 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_5`, field: RACE, label: 'Race 5 (AIAN)', value: 5 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_6`, field: RACE, label: 'Race 6 (NHPI)', value: 6 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_7`, field: RACE, label: 'Race 7 (2+ Races)', value: 7 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_8`, field: RACE, label: 'Race 8 (International)', value: 8 },
  { uniqueField: `${summaryStatsPrefix}${RACE}_9`, field: RACE, label: 'Race 9 (Unknown)', value: 9 },
  {
    uniqueField: `${summaryStatsPrefix}${DISPLAY_SCORE}`,
    field: DISPLAY_SCORE,
    label: 'Display Score',
    value: 'mean',
  },
  {
    uniqueField: `${summaryStatsPrefix}${ADMITTED}_0`,
    field: ADMITTED,
    label: 'Admitted 0 (Not Admitted)',
    value: 0,
  },
  { uniqueField: `${summaryStatsPrefix}${ADMITTED}_1`, field: ADMITTED, label: 'Admitted 1 (Admitted)', value: 1 },
  {
    uniqueField: `${summaryStatsPrefix}${ENROLLED}_0`,
    field: ENROLLED,
    label: 'Enrolled 0 (Not Enrolled)',
    value: 0,
  },
  { uniqueField: `${summaryStatsPrefix}${ENROLLED}_1`, field: ENROLLED, label: 'Enrolled 1 (Enrolled)', value: 1 },
  {
    uniqueField: `${summaryStatsPrefix}first_year_gpa`,
    field: FIRST_YEAR_GPA,
    label: 'First_Year_GPA (Mean FYGPA)',
    value: 'mean',
  },
  {
    uniqueField: `${summaryStatsPrefix}${SOPHOMORE_RETENTION}_0`,
    field: SOPHOMORE_RETENTION,
    label: 'Sophomore_Retention 0 (Not Retained)',
    value: 0,
  },
  {
    uniqueField: `${summaryStatsPrefix}${SOPHOMORE_RETENTION}_1`,
    field: SOPHOMORE_RETENTION,
    label: 'Sophomore_Retention 1 (Retained)',
    value: 1,
  },
]

export const summaryStatisticsYoyHistStageFields = [
  {
    uniqueField: `${summaryStatsPrefix}${YOY_USABLE}`,
    field: YOY_USABLE,
    label: 'YOY Usable %',
    value: 1,
  },
  {
    uniqueField: `${summaryStatsPrefix}${HIST_USABLE}`,
    field: HIST_USABLE,
    label: 'Historical Usable %',
    value: 1, // just a placeholder
  },
  {
    uniqueField: `${summaryStatsPrefix}${STAGE}`,
    field: STAGE,
    label: 'Stage',
    value: 1,
  },
]

export const summaryStatisticsInfoAdmin = [...summaryStatisticsYoyHistStageFields, ...summaryStatisticsInfo]

export const summaryStatisticsReviewInfoAdmin = [
  {
    uniqueField: `${reviewStatusPrefix}${REVIEWED}`,
    field: `${reviewStatusPrefix}${REVIEWED}`,
    label: 'Reviewed',
    value: 1,
  },
  {
    uniqueField: `${reviewStatusPrefix}${BEST}`,
    field: `${reviewStatusPrefix}${BEST}`,
    label: 'Best',
    value: 1,
  },
  {
    uniqueField: `${reviewStatusPrefix}${YOY_USABLE}`,
    field: `${reviewStatusPrefix}${YOY_USABLE}`,
    label: 'YOY Usable',
    value: 1,
  },
  {
    uniqueField: `${reviewStatusPrefix}${HIST_USABLE}`,
    field: `${reviewStatusPrefix}${HIST_USABLE}`,
    label: 'Historical Usable',
    value: 1,
  },
]

// see comment above "getNames" function for expl. on what this returns.
// export const summaryStatisticsLabels = [...getNames(summaryStatisticsInfo, 'label')]
export const summaryStatisticsFields = [...getNames(summaryStatisticsInfo, 'uniqueField')]
// export const summaryStatisticsLabelsAdmin = [...getNames(summaryStatisticsInfoAdmin, 'label')]
export const summaryStatisticsFieldsAdmin = [...getNames(summaryStatisticsInfoAdmin, 'field')]

// arrays of all fields and labels
export const allFieldsAdmin = union(missingnessFieldsAdmin, outOfBoundsFields, summaryStatisticsFieldsAdmin)
